import { el, tel } from "./dom.ts";
import type { TranslationKey } from "./i18n.ts";

/**
 * A dashboard section: always visible, never collapsed or locked.
 *
 * Replaces the earlier cascading-wizard panel (collapsible.ts/panel-flow.ts)
 * — everything the user needs (sign in, what to export, destination +
 * start) is on screen at once, ninite-style, rather than gated one step at
 * a time behind an accordion. The only gating left is on the Start button
 * itself: it stays disabled with an inline reason until sign-in, selection,
 * access and a folder are all in place, the same way ninite's own install
 * button is simply inert until something is checked.
 *
 * Still keeps a status badge in the header (setState) — knowing "Signed in
 * as X" or "Ready" at a glance is useful independent of whether the section
 * needs to be gated shut.
 */

export interface Section {
    readonly root: HTMLElement;
    readonly body: HTMLElement;
    /** Updates the header status badge. `complete` only affects styling, not visibility. */
    setState(complete: boolean, summary: string): void;
}

export function createSection(options: {
    id: string;
    titleKey: TranslationKey;
    introKey?: TranslationKey;
    icon?: string;
}): Section {
    const summaryText = el("span", { class: "panel-summary" });
    const titleEl = tel("span", options.titleKey, { class: "panel-title" });

    const left: (Node | string)[] = [];
    if (options.icon) {
        const iconSpan = document.createElement("span");
        iconSpan.className = "section-icon";
        iconSpan.innerHTML = options.icon;
        left.push(iconSpan);
    }
    left.push(titleEl);

    const header = el("div", { class: "section-header" }, [
        el("div", { class: "section-header-left" }, left),
        summaryText
    ]);

    const body = el("div", { class: "panel-body" });
    if (options.introKey) {
        body.append(tel("p", options.introKey, { class: "intro" }));
    }

    const root = el("section", { class: "panel dashboard-section", id: options.id }, [header, body]);

    return {
        root,
        body,
        setState(complete, text) {
            summaryText.textContent = text;
            root.classList.toggle("is-complete", complete);
        }
    };
}
